/**
 * WhiteRank AI Traffic Tracking Script
 * Detects AI bots and sends tracking data to WhiteRank API
 */

(function (global) {
  'use strict';

  // AI Bot detection patterns
  const AI_BOT_PATTERNS = [
    { pattern: /GPTBot|ChatGPT/i, name: 'GPTBot' },
    { pattern: /ClaudeBot|Claude-Web|anthropic-ai/i, name: 'ClaudeBot' },
    { pattern: /Google-Extended/i, name: 'Google-Extended' },
    { pattern: /PerplexityBot/i, name: 'PerplexityBot' },
    { pattern: /Bingbot.*bing/i, name: 'Bingbot' },
    { pattern: /CCBot|CommonCrawl/i, name: 'CCBot' },
    { pattern: /Applebot-Extended/i, name: 'Applebot-Extended' },
    { pattern: /Omgilibot/i, name: 'Omgilibot' },
    { pattern: /FacebookBot|facebookexternalhit/i, name: 'FacebookBot' },
    { pattern: /Diffbot/i, name: 'Diffbot' },
    { pattern: /Bytespider/i, name: 'Bytespider' },
    { pattern: /SemrushBot/i, name: 'SemrushBot' },
    { pattern: /AhrefsBot/i, name: 'AhrefsBot' },
    { pattern: /MauiBot/i, name: 'MauiBot' },
    { pattern: /anthropic/i, name: 'Anthropic' },
    { pattern: /cohere/i, name: 'Cohere' },
    { pattern: /YouBot/i, name: 'YouBot' },
    { pattern: /ScaleBot/i, name: 'ScaleBot' },
    { pattern: /Amazonbot/i, name: 'Amazonbot' },
    { pattern: /Omgilibot/i, name: 'Omgilibot' }
  ];

  /**
   * Detect if the current visitor is an AI bot
   * @returns {Object} { isAIBot: boolean, botType: string|null }
   */
  function detectAIBot() {
    const userAgent = navigator.userAgent || '';
    
    for (const bot of AI_BOT_PATTERNS) {
      if (bot.pattern.test(userAgent)) {
        return { isAIBot: true, botType: bot.name };
      }
    }
    
    return { isAIBot: false, botType: null };
  }

  /**
   * Collect browser and page information
   * @returns {Object} Collected data
   */
  function collectData() {
    const botInfo = detectAIBot();
    const now = new Date();
    const pageLoadTime = now.getTime();
    
    return {
      userAgent: navigator.userAgent || '',
      platform: navigator.platform || 'unknown',
      language: navigator.language || navigator.userLanguage || 'unknown',
      screenResolution: screen.width + 'x' + screen.height,
      viewport: window.innerWidth + 'x' + window.innerHeight,
      url: window.location.href,
      referrer: document.referrer || '',
      title: document.title || '',
      path: window.location.pathname || '/',
      isAIBot: botInfo.isAIBot,
      botType: botInfo.botType,
      timestamp: now.toISOString(),
      timeOnPage: 0
    };
  }

  /**
   * Send tracking data to the API
   * @param {Object} data - Tracking data
   * @param {string} apiEndpoint - API endpoint URL
   */
  function sendData(data, apiEndpoint) {
    const payload = JSON.stringify(data);
    
    // Use sendBeacon for reliable transmission (especially on page unload)
    if (navigator.sendBeacon && navigator.sendBeacon(apiEndpoint, payload)) {
      return;
    }
    
    // Fallback to fetch API
    if (typeof fetch !== 'undefined') {
      fetch(apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: payload,
        keepalive: true
      }).catch(function(error) {
        // Silently fail - don't interrupt user experience
        if (typeof console !== 'undefined' && console.error) {
          console.error('WhiteRank tracking error:', error);
        }
      });
    }
  }

  /**
   * Get API endpoint
   * The API endpoint is on WhiteRank's third-party service
   * @returns {string} API endpoint URL (https://whiterank.io/api/v1/whiterank/js/track-event)
   */
  function getApiEndpoint() {
    // API endpoint is on WhiteRank's third-party service
    return 'https://whiterank.io/api/v1/whiterank/js/track-event';
  }

  /**
   * Initialize tracking
   */
  function init() {
    // Check for configuration
    const config = global.whiterankData;
    
    if (!config) {
      if (typeof console !== 'undefined' && console.warn) {
        console.warn('WhiteRank: Missing configuration. Please define window.whiterankData');
      }
      return;
    }

    if (!config.siteId || !config.domain) {
      if (typeof console !== 'undefined' && console.warn) {
        console.warn('WhiteRank: Missing required configuration (siteId or domain)');
      }
      return;
    }

    // API endpoint is on WhiteRank's third-party service
    const apiEndpoint = getApiEndpoint();
    const pageLoadTime = Date.now();
    let timeOnPage = 0;
    let lastActivityTime = Date.now();
    let activityTimer = null;

    // Collect initial data
    const initialData = collectData();
    initialData.siteId = config.siteId;
    initialData.domain = config.domain;

    // Send initial tracking data
    sendData(initialData, apiEndpoint);

    // Track time on page
    function updateTimeOnPage() {
      timeOnPage = Math.floor((Date.now() - pageLoadTime) / 1000);
    }

    // Send activity update
    function sendActivityUpdate() {
      updateTimeOnPage();
      const activityData = collectData();
      activityData.siteId = config.siteId;
      activityData.domain = config.domain;
      activityData.timeOnPage = timeOnPage;
      sendData(activityData, apiEndpoint);
    }

    // Track user activity
    function onActivity() {
      lastActivityTime = Date.now();
      
      // Clear existing timer
      if (activityTimer) {
        clearTimeout(activityTimer);
      }
      
      // Set new timer for activity update (30 seconds of inactivity)
      activityTimer = setTimeout(function() {
        sendActivityUpdate();
      }, 30000);
    }

    // Listen for user activity
    ['click', 'scroll', 'keydown', 'mousemove', 'touchstart'].forEach(function(event) {
      document.addEventListener(event, onActivity, { passive: true });
    });

    // Send final data on page unload
    function onBeforeUnload() {
      updateTimeOnPage();
      const finalData = collectData();
      finalData.siteId = config.siteId;
      finalData.domain = config.domain;
      finalData.timeOnPage = timeOnPage;
      sendData(finalData, apiEndpoint);
    }

    // Use pagehide for better mobile support
    window.addEventListener('pagehide', onBeforeUnload);
    window.addEventListener('beforeunload', onBeforeUnload);

    // Log initialization
    if (typeof console !== 'undefined' && console.log) {
      console.log('WhiteRank AI Traffic Tracker initialized for: ' + config.domain);
    }
  }

  // Auto-initialize in browser environments
  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      // DOM already loaded
      init();
    }
  }

  // Export for module systems (ESM/CommonJS)
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { init, detectAIBot, collectData };
  }
  
  if (typeof exports !== 'undefined') {
    exports.init = init;
    exports.detectAIBot = detectAIBot;
    exports.collectData = collectData;
  }

})(typeof window !== 'undefined' ? window : typeof global !== 'undefined' ? global : this);

